import { useState, useEffect, ReactNode } from "react";
import getFeatureFlags from "@api/getFeatureFlags";
import getTpUser from "@api/getTpUser";
import { APP_BASE_URL } from "@config";
import { navigateTo } from "@utils/navigate";
import rollbar from "@utils/rollbar";
import { FEATURE_FLAG_BILL_PORT_DENY_LIST } from "@constants/featureFlags";

type AccessState = "pending" | "allowed" | "denied";

export const AccessGuard = ({ children }: { children: ReactNode }) => {
  const [accessState, setAccessState] = useState<AccessState>("pending");

  useEffect(() => {
    const controller = new AbortController();

    const checkAccess = async () => {
      const response = await getFeatureFlags(controller.signal);

      if (response.error === "AbortError") return;

      const isDenied = (response.data ?? []).some(
        (flag) => flag.name === FEATURE_FLAG_BILL_PORT_DENY_LIST && flag.enabled,
      );

      if (isDenied) {
        // Only fetch the user on denial, so that the warning carries the
        // personId without adding a request for allowed users.
        const userResponse = await getTpUser(controller.signal);

        if (userResponse.error === "AbortError") return;

        rollbar.warning("Access denied by feature flag", {
          featureFlag: FEATURE_FLAG_BILL_PORT_DENY_LIST,
          personId: userResponse.data?.user.personId,
        });
        setAccessState("denied");
        navigateTo(`${APP_BASE_URL}/`);
        return;
      }

      setAccessState("allowed");
    };

    checkAccess();
    return () => controller.abort();
  }, []);

  // Block the child tree from mounting until the deny list check has settled so
  // that denied users never issue billing portal API calls.
  if (accessState !== "allowed") return null;

  return <>{children}</>;
};
